// Drawer used while the user composes an editable draft (scratchpad
// model). Mounted as an overlay sibling to the list when
// `useRequestsStore.draft` is non-null. Hosts the DraftEditor and owns
// the close-side cleanup contract so every close affordance routes
// through the same handler.
//
// @aiPurpose Render the editable draft scratchpad in a Drawer.
// @aiInvariant Every close affordance (Esc, backdrop, X, DraftEditor's
//   "Discard") routes through `requestClose`, which:
//     1. confirms with the user when the scratchpad has unsaved
//        changes (dirty=true), so a stray click never silently drops
//        local edits;
//     2. ships unsaved photo keys to the cleanup endpoints so the
//        close path never leaks orphan storage objects — never-saved
//        scratchpads use `cleanupNeverSavedDraftPhotos`, persisted
//        drafts use `cleanupPersistedDraftPhotos(persistedId)`;
//     3. closes the scratchpad through `closeDraft` once cleanup
//        settles. Cleanup failures are logged inline but do NOT
//        block the close — the persisted DRAFT row remains
//        authoritative on the server, and the user's intent
//        ("discard local changes") is honored regardless.
// @aiInvariant (#1594) Every close/delete guard derives from the single
//   serialized `draftActivity` phase + `pendingPhotoUploads`. `requestClose`
//   is a no-op unless `canClose(activity, pendingPhotoUploads)` — i.e.
//   the machine is `'idle'` and no upload is racing. Closing during an
//   in-flight save would unmount the scratchpad before the response lands
//   and re-introduce exactly the orphan-DRAFT problem this flow prevents.
// @aiInvariant `beforeunload` listener attached when the scratchpad
//   is dirty — closing the tab/reload triggers the browser's native
//   unload prompt so a hard navigation cannot silently drop edits.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from 'react-router';
import { Drawer, IconButton } from '../../../ui/index.js';
import { useRequestsStore, isDraftDirty } from '../store.js';
import { canBeginDelete, canClose, isBusy } from '../draft-activity.js';
import { RequestStatusPill } from './RequestStatusPill.js';
import { DraftEditor } from './DraftEditor.js';
import { DraftCloseConfirmModal } from './DraftCloseConfirmModal.js';
import {
  cleanupNeverSavedDraftPhotos,
  cleanupPersistedDraftPhotos,
  deleteRequest,
} from '../../../api/requests/index.js';
import { errorMessage } from '../../../api/errors.js';
import { narrowInvalidateForRequest, evictRequestRowEverywhere } from '../queries.js';
import { DRAFT_SAVE_ERROR_INSURANCE_TRIPLET, useDraftSave } from '../useDraftSave.js';
import { RequestStatus } from '@magimp/shared';
import type { UserRef } from '../types.js';

export interface DraftDrawerProps {
  narrow: boolean;
  me: UserRef;
}

export function DraftDrawer({ narrow, me }: DraftDrawerProps) {
  const { t } = useTranslation(['requests', 'common']);
  const draft = useRequestsStore((s) => s.draft);
  const baselineDraft = useRequestsStore((s) => s.baselineDraft);
  const draftPersistedId = useRequestsStore((s) => s.draftPersistedId);
  // WHY (#1594): the single serialized lifecycle phase. `closing` /
  //   `deleting` and the old `draftSending` / `draftSaving` booleans all
  //   derive from it; `closeError` / `deleteError` / `confirmSaveError`
  //   stay local error state (they are not lifecycle phases).
  const activity = useRequestsStore((s) => s.draftActivity);
  const beginActivity = useRequestsStore((s) => s.beginDraftActivity);
  const settleActivity = useRequestsStore((s) => s.settleDraftActivity);
  const pendingPhotoUploads = useRequestsStore((s) => s.pendingPhotoUploads);
  const unsavedPhotoKeys = useRequestsStore((s) => s.unsavedPhotoKeys);
  const closeDraft = useRequestsStore((s) => s.closeDraft);
  const pendingDraftCloseFollowUp = useRequestsStore((s) => s.pendingDraftCloseFollowUp);
  const setPendingFollowUp = useRequestsStore((s) => s.setPendingDraftCloseFollowUp);
  const qc = useQueryClient();
  const saveDraft = useDraftSave();
  const [closeError, setCloseError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSaveError, setConfirmSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const dirty = isDraftDirty(draft, baselineDraft);

  // INVARIANT (#521): RR `useBlocker` covers every router-driven exit
  // — list row click that calls `urlState.openRequest`, drawer
  // X-and-route changes, browser back/forward, in-app links. The
  // `<a href>` style anchors in the app already route through RR's
  // <Link>/`navigate`, so the blocker fires uniformly. Block when
  // EITHER the scratchpad is dirty OR a photo upload is racing —
  // navigating during an in-flight PUT would let the resolved key
  // land in `unsavedPhotoKeys` AFTER the route change, leaking the
  // storage object past cleanup.
  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string; search: string };
        nextLocation: { pathname: string; search: string };
      }) => {
        if (
          currentLocation.pathname === nextLocation.pathname &&
          currentLocation.search === nextLocation.search
        ) {
          return false;
        }
        // WHY (#1594): while any lifecycle action is in flight, let the
        //   navigation pass — the in-flight action owns its own close, so
        //   re-blocking here would deadlock the route change.
        if (isBusy(activity)) return false;
        return dirty || pendingPhotoUploads > 0;
      },
      [activity, dirty, pendingPhotoUploads],
    ),
  );

  // When the blocker trips, route through the same store-level
  // follow-up channel that the list page uses for non-router exits.
  // The modal opens via the `pendingDraftCloseFollowUp` effect below,
  // and `proceed`/`reset` resolve the navigation when the user picks
  // an action.
  const blockerProceedRef = useRef<(() => void) | null>(null);
  const blockerResetRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (blocker.state === 'blocked') {
      blockerProceedRef.current = blocker.proceed ?? null;
      blockerResetRef.current = blocker.reset ?? null;
      // Park the navigation as the close follow-up. If save/discard
      // wins, we call `proceed`; if cancel wins, we call `reset` and
      // clear the follow-up.
      setPendingFollowUp(() => {
        const proceed = blockerProceedRef.current;
        blockerProceedRef.current = null;
        blockerResetRef.current = null;
        proceed?.();
      });
    }
  }, [blocker, setPendingFollowUp]);

  // beforeunload guard: only attached while dirty so a clean
  // scratchpad never triggers the browser's "unsaved changes" prompt.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Modern browsers ignore the returnValue string but require it
      // to be set (truthy) for the prompt to fire. Native dialog
      // copy is browser-controlled.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  async function performClose(opts: { discardUnsavedPhotos: boolean }): Promise<void> {
    // INVARIANT (#1594): claim the machine for the close. Callers
    //   (`requestClose`, `onConfirmSave`, `onConfirmDiscard`) gate on
    //   `canClose` first; a rejected begin here is a defensive no-op.
    if (!beginActivity('begin-close')) return;
    setCloseError(null);
    try {
      if (opts.discardUnsavedPhotos && unsavedPhotoKeys.length > 0) {
        try {
          if (draftPersistedId !== null) {
            await cleanupPersistedDraftPhotos(draftPersistedId, {
              objectKeys: unsavedPhotoKeys,
            });
          } else {
            await cleanupNeverSavedDraftPhotos({ objectKeys: unsavedPhotoKeys });
          }
        } catch (err) {
          // Cleanup is best-effort by Resolved Decision #17 — surface
          // the error inline but proceed with the close so the user's
          // discard intent isn't held hostage by a transient storage
          // problem. The persisted DRAFT row (if any) keeps its
          // saved-baseline photos.
          setCloseError(errorMessage(err));
        }
      }
      if (draftPersistedId !== null) {
        narrowInvalidateForRequest(qc, draftPersistedId);
      }
      closeDraft();
    } finally {
      // INVARIANT (#1594): `closeDraft` already reset the machine to
      //   `'idle'` on the happy path; settle again is an idempotent no-op
      //   that also recovers the machine if an exception skipped the
      //   close (cleanup is best-effort and caught above, so this is the
      //   defensive belt-and-braces path).
      settleActivity();
    }
  }

  // INVARIANT: destructive delete intent is separate from the close
  // intent. Triggered by the editor's two-step armed
  // `requests.draft.deleteDraft` button. For a persisted DRAFT it
  // best-effort cleans the unsaved-photo batch, then issues
  // `DELETE /requests/:id`. For a never-saved scratchpad it routes
  // to the same close path because there is no server row to delete
  // — the user's intent (destroy this draft) is honored by clearing
  // local state and uploaded unsaved photos.
  async function performDelete(): Promise<void> {
    // INVARIANT (#1594): destructive delete may begin only from `'idle'`
    //   with no upload racing — `canBeginDelete` is the single guard that
    //   replaced the old `deleting || closing || draftSaving ||
    //   draftSending || pendingPhotoUploads > 0` chain.
    if (!canBeginDelete(activity, pendingPhotoUploads)) return;
    if (draftPersistedId === null) {
      // No server row → same flow as a clean discard close. Reuse
      // performClose (which claims its own `'closing'` phase) to keep the
      // cleanup contract identical.
      await performClose({ discardUnsavedPhotos: true });
      return;
    }
    if (!beginActivity('begin-delete')) return;
    setDeleteError(null);
    setCloseError(null);
    const id = draftPersistedId;
    try {
      // Best-effort cleanup of unsaved upload keys. Cleanup failure
      // does NOT block the destructive delete — the user's delete
      // intent must win even if the cleanup endpoint is transient-
      // broken. INVARIANT: both persisted DRAFT and persisted RETURN
      // are draft-like for the cleanup endpoint;
      // `cleanupPersistedDraftPhotos` accepts both statuses.
      if (
        (draft?.status === RequestStatus.DRAFT || draft?.status === RequestStatus.RETURN) &&
        unsavedPhotoKeys.length > 0
      ) {
        try {
          await cleanupPersistedDraftPhotos(id, { objectKeys: unsavedPhotoKeys });
        } catch (err) {
          setCloseError(errorMessage(err));
        }
      }
      await deleteRequest(id);
      evictRequestRowEverywhere(qc, id);
      closeDraft();
    } catch (err) {
      // DELETE failed → keep the drawer open and surface the error so
      // the user can retry or close intentionally. Do NOT call
      // closeDraft.
      setDeleteError(errorMessage(err));
    } finally {
      // INVARIANT (#1594): settle back to `'idle'`. On success
      //   `closeDraft` already reset it; on the DELETE-error path this is
      //   the ONLY reset, returning the machine to `'idle'` with the
      //   drawer still open so the user can retry.
      settleActivity();
    }
  }

  // The dirty-close modal opens whenever the user attempts to close
  // (X/Esc/backdrop) on a dirty scratchpad OR a follow-up navigation
  // (router blocker, draft-row swap) is parked.
  useEffect(() => {
    if (pendingDraftCloseFollowUp !== null) {
      setConfirmSaveError(null);
      setConfirmOpen(true);
    }
  }, [pendingDraftCloseFollowUp]);

  function runFollowUpAndClear(): void {
    const fn = useRequestsStore.getState().pendingDraftCloseFollowUp;
    setPendingFollowUp(null);
    if (fn) fn();
  }

  function requestClose(): void {
    // INVARIANT (#521 + #1594): block close while ANY lifecycle action is
    // in flight OR a storage PUT is racing. `canClose` is the single
    // guard: machine `'idle'` AND `pendingPhotoUploads === 0`. Closing
    // during an in-flight upload would leave `closeDraft` to wipe the
    // scratchpad before the upload-resolved key reached
    // `unsavedPhotoKeys`, leaking the storage object past cleanup; closing
    // during a save/send/delete would race those handlers.
    if (!canClose(activity, pendingPhotoUploads)) return;
    if (dirty) {
      setConfirmSaveError(null);
      setConfirmOpen(true);
      return;
    }
    void performClose({ discardUnsavedPhotos: true });
  }

  async function onConfirmSave(): Promise<void> {
    // WHY (#1594): the save+close path runs `saveDraft` (claims `'saving'`)
    //   then `performClose` (claims `'closing'`). Only start from `'idle'`;
    //   the modal's own `buttonsDisabled` gates the upload-in-flight case.
    if (isBusy(activity)) return;
    setConfirmSaveError(null);
    const outcome = await saveDraft();
    if (!outcome.ok) {
      // Save failed — keep the modal open so the user can retry,
      // discard, or return to editing. Never silently discard.
      if (outcome.error) {
        // WHY (#1061): translate the insurance-triplet sentinel into
        //   the same localised error the editor surfaces on Save, so
        //   the dirty-close modal does not display a raw English
        //   placeholder.
        setConfirmSaveError(
          outcome.error === DRAFT_SAVE_ERROR_INSURANCE_TRIPLET
            ? t('requests:draft.required.insuranceTriplet')
            : outcome.error,
        );
      }
      return;
    }
    // After a successful save, every unsaved key has been narrowed
    // out of `unsavedPhotoKeys` by `applySaveResult`. Pass
    // `discardUnsavedPhotos: true` so any keys that remain (e.g.
    // ones whose owning items were removed before save) still reach
    // cleanup.
    setConfirmOpen(false);
    await performClose({ discardUnsavedPhotos: true });
    runFollowUpAndClear();
  }

  async function onConfirmDiscard(): Promise<void> {
    setConfirmOpen(false);
    await performClose({ discardUnsavedPhotos: true });
    runFollowUpAndClear();
  }

  function onConfirmCancel(): void {
    // INVARIANT (#529): cancel must mirror the busy discipline that
    // visible buttons and the modal-level dismissDisabled enforce.
    // While a save or photo upload is racing, even the "cancel" path
    // is suppressed so the modal stays a stable surface and a stray
    // Esc/backdrop never lands the user back on the editor while
    // cleanup or save is mid-flight. WHY (#1594): `canClose` is the same
    // idle-and-no-upload gate the close path uses.
    if (!canClose(activity, pendingPhotoUploads)) return;
    setConfirmOpen(false);
    setConfirmSaveError(null);
    // If the modal was opened by a router blocker, reset the blocker
    // so the user stays put; for non-router callers there's no
    // blocker to reset, only the follow-up to drop.
    const reset = blockerResetRef.current;
    blockerProceedRef.current = null;
    blockerResetRef.current = null;
    setPendingFollowUp(null);
    reset?.();
  }

  if (!draft) return null;

  return (
    <Drawer
      onClose={() => {
        requestClose();
      }}
      narrow={narrow}
      width={880}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minWidth: 0,
          background: 'var(--bg-1)',
        }}
      >
        <div
          style={{
            padding: '16px 24px 12px',
            borderBottom: '1px solid var(--border-1)',
            flex: 'none',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 6,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-3)' }}>
                {draftPersistedId ?? '—'}
              </span>
              {/* INVARIANT (#536): pill mirrors the row's actual
                  status so the user never sees a `RETURN` row labeled
                  as `DRAFT`. Never-saved scratchpads still init as
                  `DRAFT` per `openDraft`. */}
              <RequestStatusPill status={draft.status} />
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 600,
                color: 'var(--fg-1)',
                letterSpacing: '-.005em',
                lineHeight: 1.25,
              }}
            >
              {draft.name || t('requests:common.noName')}
            </h2>
            {draft.status === RequestStatus.RETURN && draft.returnReason !== null ? (
              <ReturnReasonBanner reason={draft.returnReason} />
            ) : null}
          </div>
          <IconButton
            icon="x"
            label={t('common:actions.close')}
            data-testid="draft-drawer-close"
            onClick={() => {
              requestClose();
            }}
            // WHY (#1594): mirror `requestClose`'s `canClose` guard so the
            //   button never promises an action the handler would swallow.
            disabled={!canClose(activity, pendingPhotoUploads)}
          />
        </div>

        <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '16px 24px 24px' }}>
          <DraftEditor
            scratchpad={draft}
            me={me}
            onDelete={() => {
              void performDelete();
            }}
            closeError={closeError}
            deleteError={deleteError}
            uploading={pendingPhotoUploads > 0}
          />
        </div>
      </div>
      <DraftCloseConfirmModal
        open={confirmOpen}
        persisted={draftPersistedId !== null}
        saving={activity === 'saving'}
        uploading={pendingPhotoUploads > 0}
        saveError={confirmSaveError}
        onSaveAndClose={() => {
          void onConfirmSave();
        }}
        onDiscardAndClose={() => {
          void onConfirmDiscard();
        }}
        onCancel={onConfirmCancel}
      />
    </Drawer>
  );
}

// WHY (#536): a returned-row editor must surface the prior return
// reason as read-only context; the manager cannot fix the request
// without seeing why it came back. Rendered inline in the drawer
// header (under the title) so it remains visible even when the user
// scrolls within the editor body.
function ReturnReasonBanner({ reason }: { reason: string }) {
  const { t } = useTranslation(['requests']);
  return (
    <div
      role="note"
      style={{
        marginTop: 10,
        padding: '10px 12px',
        background: 'var(--status-warn-bg, var(--bg-2))',
        border: '1px solid var(--border-2)',
        borderRadius: 'var(--r-2)',
        fontSize: 12,
        color: 'var(--fg-2)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
      }}
      data-testid="draft-return-reason-banner"
    >
      <span style={{ fontWeight: 600, color: 'var(--fg-1)' }}>
        {t('requests:draft.returnReasonBanner.title')}
      </span>
      <span style={{ color: 'var(--fg-3)' }}>
        {t('requests:draft.returnReasonBanner.bodyPrefix')}{' '}
        <span style={{ color: 'var(--fg-1)' }}>{reason}</span>
      </span>
    </div>
  );
}
